import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Globe2, Layers, Building2, Users } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { MediaImage } from "../components/MediaImage";
import { SectionHeading, CTASection } from "../components/ui-bits";

const HERO_BG = "https://images.unsplash.com/photo-1750688222204-4928ecbd1034?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const STATS = [
  { icon: Globe2, value: "25+", label: "Countries" },
  { icon: Layers, value: "13", label: "Verticals" },
  { icon: Building2, value: "40+", label: "Companies" },
  { icon: Users, value: "1000+", label: "Professionals" },
];

export default function Home() {
  const [verticals, setVerticals] = useState([]);
  const [news, setNews] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    api.get("/verticals").then((r) => setVerticals(r.data)).catch(() => {});
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/blog").then((r) => setBlog(r.data)).catch(() => {});
  }, []);

  const featuredVerticals = verticals.slice(0, 8);
  const latest = [...news, ...blog].slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 hero-radial" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/40 via-[#030712]/70 to-[#030712]" />
          <div className="absolute inset-0 grid-lines opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="eyebrow mb-6"
          >
            Futuristic Technology Solutions
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-display font-extrabold tracking-tight leading-[1.02] text-white max-w-5xl"
          >
            The World of <span className="text-gradient-emerald">SRAM &amp; MRAM</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed"
          >
            A global conglomerate with international alliances and interconnected verticals — spanning
            medical technology, water, enterprise software, infrastructure, resources and hospitality.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/verticals" data-testid="hero-verticals-btn" className="btn-emerald px-7 py-3.5 rounded-full inline-flex items-center gap-2">
              Explore Verticals <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/about" data-testid="hero-about-btn" className="px-7 py-3.5 rounded-full border border-slate-700 text-slate-200 hover:border-emerald-500/50 transition-colors">
              Who We Are
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-slate-800 bg-[#050810]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="flex flex-col" data-testid={`stat-${s.label.toLowerCase()}`}>
                <s.icon className="w-6 h-6 text-emerald-400 mb-3" />
                <span className="font-mono text-4xl font-bold text-white">{s.value}</span>
                <span className="text-sm text-slate-500 mt-1 uppercase tracking-wider">{s.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-8">
        <Reveal>
          <div className="card-vertical rounded-2xl p-10 h-full">
            <p className="eyebrow mb-4">Our Mission</p>
            <p className="text-xl text-slate-200 leading-relaxed font-display">
              Bridging financial and technical know-how by balancing the synergy of success — enhancing
              resources, technology, value and wealth amongst competitive peers.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-vertical rounded-2xl p-10 h-full">
            <p className="eyebrow mb-4">Our Vision</p>
            <p className="text-xl text-slate-200 leading-relaxed font-display">
              Identify innovative business verticals, invest in futuristic technologies, create
              opportunities and empower talents to achieve and sustain market-leading positions.
            </p>
          </div>
        </Reveal>
      </section>

      {/* VERTICALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeading
          eyebrow="Our Business Verticals"
          title="A global conglomerate across finance, technology, energy and resources."
          right={<Link to="/verticals" data-testid="home-all-verticals-btn" className="text-emerald-400 hover:text-emerald-300 text-sm font-mono inline-flex items-center gap-1">All verticals <ArrowUpRight className="w-4 h-4" /></Link>}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredVerticals.map((v, i) => (
            <Reveal key={v.id} delay={(i % 4) * 0.06}>
              <Link
                to={`/verticals/${v.slug}`}
                data-testid={`home-vertical-card-${v.slug}`}
                className="card-vertical rounded-2xl overflow-hidden group flex flex-col h-full"
              >
                <div className="relative h-40 overflow-hidden">
                  <MediaImage src={v.image_url} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D131F] to-transparent" />
                  <span className="absolute bottom-3 left-4 text-[11px] font-mono uppercase tracking-widest text-emerald-400">{v.category}</span>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-white group-hover:text-emerald-400 transition-colors">{v.name}</h3>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-2">{v.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NEWSROOM PREVIEW */}
      {latest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHeading
            eyebrow="From SRAM & MRAM"
            title="Newsroom & Insights"
            right={<Link to="/newsroom" data-testid="home-all-news-btn" className="text-emerald-400 hover:text-emerald-300 text-sm font-mono inline-flex items-center gap-1">View all <ArrowUpRight className="w-4 h-4" /></Link>}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latest.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.08}>
                <Link to={`/newsroom/${n.slug}`} data-testid={`home-news-card-${n.slug}`} className="card-vertical rounded-2xl overflow-hidden group block h-full">
                  <div className="h-48 overflow-hidden">
                    <MediaImage src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-xs text-emerald-400 mb-2">{n.date}</p>
                    <h3 className="font-display font-semibold text-white line-clamp-3 group-hover:text-emerald-400 transition-colors">{n.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
